/**
 * FlowWeightedItemRow
 *
 * Shared row for Landers/Offers — mirrors FlowPathsPathRow list styling.
 * In preview/overlay mode it uses the flat Current/New/Diff grid (matching the
 * Flow Optimizer preview) so the FlowOverlayMetricLabels header lines up; the
 * profit badge moves onto the name line there. Non-preview layout is unchanged.
 */

import { FC, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { FlowLayer } from '@trafficpulse/shared';

import { Switch } from '@/components/ui';
import { cn } from '@/lib/utils';
import { usePreviewMode, usePreviewRank, usePreviewRow } from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import { PreviewRowHoverCard } from '@/features/flow-optimizer/components/preview/hover';
import PreviewRankBadge from '@/features/flow-optimizer/components/preview/PreviewRankBadge';
import PreviewExcludedIndicator from '@/features/flow-optimizer/components/preview/PreviewExcludedIndicator';
import PreviewWeightCells from '@/features/flow-optimizer/components/preview/PreviewWeightCells';
import { FlowTagChips } from '@/features/flow-optimizer/components/preview/FlowTagChips';
import {
  excludedReasonText,
  scopePreviewRowToLander,
  showExcludedBadge,
} from '@/features/flow-optimizer/components/preview/preview-row.types';

import { FlowRowPreviewWeight, useFlowRowPreviewLocked } from '../../common/FlowRowPreviewWeight';
import FlowItemStatsPanel from '../FlowItemStatsPanel';
import { FlowPathsProfitTag } from '../flow-paths/FlowPathsPanel.parts';

import type { FlowWeightedItemRowItem } from './flow-weighted-item.types';

type Metrics = Record<string, number>;

export type { FlowWeightedItemRowItem } from './flow-weighted-item.types';

export interface FlowWeightedItemRowProps {
  kind: 'lander' | 'offer';
  item: FlowWeightedItemRowItem;
  pathId?: string;
  /** Selected lander id — for offers, scopes the popup to per-(path,lander) stats. */
  landerId?: string;
  selected?: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onSelect?: () => void;
  onToggleActive?: (active: boolean) => void;
  metrics?: Metrics;
  metricsRangeLabel?: string;
  isMutating?: boolean;
}

export const FlowWeightedItemRow: FC<FlowWeightedItemRowProps> = ({
  kind,
  item,
  pathId,
  landerId,
  selected = false,
  expanded,
  onToggleExpand,
  onSelect,
  onToggleActive,
  metrics,
  metricsRangeLabel,
  isMutating = false,
}) => {
  const isActive = item.weight > 0;
  const originalWeight = item.originalWeight ?? item.weight;
  const isDirty = originalWeight !== item.weight;
  const profit = useMemo(() => metrics?.profit, [metrics]);
  const canSelectLander = kind === 'lander' && onSelect;
  const layer: FlowLayer = kind === 'lander' ? 'landers' : 'offers';
  const previewRow = usePreviewRow(layer, item.id, pathId);
  const previewMode = usePreviewMode();
  const rank = usePreviewRank(layer, item.id, pathId);
  const previewLocked = useFlowRowPreviewLocked(previewRow, isActive);
  // Offers: show the offer's stats WITHIN the selected lander (per-(path,lander)).
  const hoverRow = useMemo(
    () => scopePreviewRowToLander(previewRow, landerId),
    [previewRow, landerId],
  );

  const chevron = (
    <button
      type="button"
      className={cn(
        '-ml-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        expanded && 'text-foreground',
      )}
      onClick={onToggleExpand}
      aria-label={expanded ? 'Hide statistics' : 'Show statistics'}
    >
      {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
    </button>
  );

  const itemSwitch = onToggleActive ? (
    <Switch
      checked={isActive}
      disabled={isMutating}
      onCheckedChange={(checked) => onToggleActive(Boolean(checked))}
      aria-label={isActive ? `Disable ${kind}` : `Enable ${kind}`}
    />
  ) : null;

  // Name, status badge and ⚡ tags all share one line (wrap as a group) so the
  // tags sit next to the winning/lose badge rather than on a line below.
  const nameArea = (
    <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
      {canSelectLander ? (
        <button
          type="button"
          className={cn(
            'min-w-0 whitespace-normal break-words text-left text-xs font-medium leading-tight hover:underline',
            selected && 'text-primary',
          )}
          onClick={onSelect}
        >
          {item.name}
        </button>
      ) : (
        <span className="min-w-0 whitespace-normal break-words text-xs font-medium leading-tight">
          {item.name}
        </span>
      )}
      {showExcludedBadge(previewRow, isActive) ? (
        <PreviewExcludedIndicator title={excludedReasonText(previewRow)} />
      ) : null}
      {previewLocked ? (
        <span className="inline-flex shrink-0 items-center">
          <FlowPathsProfitTag profit={profit} />
        </span>
      ) : null}
      <FlowTagChips layer={layer} itemId={item.id} pathId={pathId} className="gap-1" />
      <span className="sr-only">ID {item.id}</span>
    </div>
  );

  return (
    <PreviewRowHoverCard name={item.name} row={hoverRow}>
    <div
      className={cn(
        'transition-colors',
        selected && 'bg-primary/5',
        isDirty && !selected && 'bg-warning/5',
      )}
    >
      <div className="w-full px-1.5 py-1">
        {previewLocked && previewRow ? (
          <div className="grid w-full items-center gap-x-1.5 grid-cols-[1.5rem_1.5rem_minmax(0,1fr)_3rem_0.75rem_3rem_3.25rem_2.75rem]">
            <PreviewRankBadge rank={rank} />
            {chevron}
            {nameArea}
            <div className="col-span-4 grid grid-cols-[3rem_0.75rem_3rem_3.25rem] items-center gap-x-1.5">
              <PreviewWeightCells row={previewRow} />
            </div>
            <div className="flex items-center justify-end">{itemSwitch}</div>
          </div>
        ) : (
          <div
            className={cn(
              'grid w-full items-center gap-x-1.5 gap-y-0',
              previewMode
                ? 'grid-cols-[1.5rem_auto_minmax(0,1fr)_auto]'
                : 'grid-cols-[auto_minmax(0,1fr)_auto]',
            )}
          >
            {previewMode ? <PreviewRankBadge rank={rank} /> : null}
            {chevron}
            {nameArea}
            <div className="flex shrink-0 items-center justify-end gap-x-1.5">
              <span className="inline-flex shrink-0 items-center">
                <FlowPathsProfitTag profit={profit} />
              </span>
              <FlowRowPreviewWeight
                previewRow={previewRow}
                active={isActive}
                fallback={
                  <div className="max-w-[5rem] shrink-0 text-right text-[11px] leading-tight text-muted-foreground tabular-nums sm:max-w-[6rem]">
                    {originalWeight === item.weight ? (
                      <span>{item.weight}%</span>
                    ) : (
                      <span className="text-warning">
                        {originalWeight}% → {item.weight}%
                      </span>
                    )}
                  </div>
                }
              />
              {itemSwitch}
            </div>
          </div>
        )}
      </div>

      <FlowItemStatsPanel open={expanded} metrics={metrics} rangeLabel={metricsRangeLabel} />
    </div>
    </PreviewRowHoverCard>
  );
};

export default FlowWeightedItemRow;
